/* eslint-disable no-undef */
/* eslint-disable max-len */
/* eslint-disable no-unused-vars */

import { PolymerElement } from "@polymer/polymer/polymer-element.js";
import { html } from "@polymer/polymer/lib/utils/html-tag.js";
import template from "./ns-search-results-page.html";
import {
  ShopContextService,
  Product,
  ProductStatusService
} from "@nuskin/ns-shop";
import { events } from "@nuskin/ns-util";
import { SitesSessionStorageService } from "@nuskin/ns-util";
import { UserService } from "@nuskin/ns-account";
import $ from "@nuskin/nuskinjquery";

const SEARCH_TERM_KEY = "searchResultsData";

/**
 * <p>The ns-search-results-page is a containing page that will display search result components such as the product-small-card</p>
 *
 * <h2>UX/Design Docs</h2>
 * <p>Need to find</p>
 *
 * <h2>Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *      <ns-search-results-page></ns-search-results-page>
 *   </template>
 * </demo-snippet>
 *
 * <marked-element>
 *  <div slot="markdown-html">
 *  <script type="text/markdown">
 *
 *      ### Styling
 *
 *      ##### `<ns-search-results-page>` provides the following custom properties and mixins for styling:
 *
 *      Custom property                 | Description                                                         | Default
 *      --------------------------------|---------------------------------------------------------------------|-------------------
 *      `--p-medium`                      | Mixin applied to .breadcrumbs-area and #popularProducts #no-result  | `See custom-global-styles`
 *      `--a-bold-caps`                 | Mixin applied to the #back button                                   | `See custom-global-styles`
 *      `--subtitle-small-bold-italic-caps`     | Mixin applied to the #popularProducts h6                            | `See custom-global-styles`
 *
 *      ### Slots
 *
 *      ##### `<ns-search-results-page>` provides the following slots for content:
 *
 *      Custom property                 | Description                           | Default
 *      --------------------------------|---------------------------------------|-------------------
 *      `popularProducts`               | Location for popular-product carousel | ``
 *
 *  </script>
 * </marked-element>
 *
 */
export class NsSearchResultsPage extends PolymerElement {
  constructor() {
    super();
  }

  ready() {
    super.ready();
    this._removeSortOptionIfNotDistributor("pointHighToLow");
    this._removeSortOptionIfNotDistributor("pointLowToHigh");
    events.subscribe(events.shop.SEARCH_EXECUTED, () => {
      this._resetSortDropdown();
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.init();
    this.addEventListener("nsDropdownSelect", this.sortOptionSelection);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("nsDropdownSelect", this.sortOptionSelection);
  }

  static get is() {
    return "ns-search-results-page";
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties() {
    return {
      /**
       * Used to provide translations to the search result page.
       */
      translations: {
        type: Object,
        value: {
          alsoInterested: "You may also be interested in:",
          back: "back",
          loadMore: "Load More",
          noResults: "Hmmm... looks like there weren't any results for:",
          searchResultsFor:
            "%searchResultsNumber% Search Results for %searchTerm%",
          sortBy: "Sort by",
          sortOptions: [
            {
              title: "Best Match",
              sort: "best",
              order: "best"
            },
            {
              title: "A-Z",
              sort: "title",
              order: "asc"
            },
            {
              title: "Z-A",
              sort: "title",
              order: "desc"
            },
            {
              title: "Price (High to Low)",
              sort: "price",
              order: "asc"
            },
            {
              title: "Price (Low to High)",
              sort: "price",
              order: "desc"
            },
            {
              title: "Point Value (High to Low)",
              sort: "pv",
              order: "asc"
            },
            {
              title: "Point Value (Low to High)",
              sort: "pv",
              order: "desc"
            }
          ]
        }
      },
      /**
       * Used to set the number of search results found and display that information to the user.
       */
      searchResultsNumber: {
        type: Number,
        notify: true,
        value: 0
      },
      /**
       * Used to show the user the search term they entered in.
       */
      query: {
        type: String,
        value: "",
        observer: "_queryUpdated"
      },
      /**
       * Used for iron-pages to show search results.
       */
      selected: {
        type: Number,
        value: 0
      },
      testResults: Array,
      /**
       * Search Result object found from Solr. It expects it to be an array of product objects.
       */
      searchResults: {
        type: Array,
        reflectToAttribute: true,
        observer: "_updateResults"
      },
      /**
       * Helper property to set CSS on grid when active.
       */
      gridActive: {
        type: Boolean,
        value: true
      },
      /**
       * Used to hide or show the sorting dropdown.
       */
      noSort: {
        type: Boolean,
        value: false
      },
      /**
       * Used to display the current sort option in the dropdown.
       */
      sortOptionSelected: {
        type: String
        /*observer: "_sortOptionSelectedUpdated"*/
      },
      /**
       * Used to hide the tabs of the different search mediums such as product and media center search.
       */
      hideTabs: {
        type: Boolean,
        value: true
      },
      /**
       * Used to hide and show the load more button for lazy loading.
       */
      hideLoadMore: {
        type: Boolean,
        value: true
      },
      /**
       * Number of cards to show on page before lazy load button must be clicked to show more.
       */
      searchResultsPerPage: {
        type: Number,
        value: 36
      },
      /**
       * String used to translate the No Results portion of the page.
       */
      srcNoResults: String,
      firstLoad: {
        type: Boolean,
        value: true
      }
    };
  }

  clickedOnSort() {
    this.firstLoad = false;
  }
  /**
   * Called upon initialization of component
   */
  init() {
    for (let i = 0; i < this.searchResults.length; i++) {
      i === 0
        ? this.set("searchResults." + i + ".active", "active")
        : this.set("searchResults.active", "inactive");
    }
    this.onResize();
    window.addEventListener("resize", (e) => {
      this.onResize();
    });
  }

  /**
   * Observer of the query property
   * @private
   */
  _queryUpdated() {
    this._updateSearchForText();
    let sortOptions = this.translations.sortOptions;
    if (this.query.length > 0) {
      this.set("translations.sortOptions", []);
      this.set("translations.sortOptions", sortOptions);
    }
    this._setLocalStorageResultsData();
    if (this.query === "dev") this.set("searchResults", this.testResults);
  }

  /**
   * Observer of the sortOptionSelected property
   * @private
   */
  _sortOptionSelectedUpdated() {
    let index = this.sortOptionSelected;
    let sort = this.translations.sortOptions[index].sort;
    let order = this.translations.sortOptions[index].order;
    this.sortBy(sort, order);
  }

  sortOptionSelection(selection) {
    this.sortOptionSelected = JSON.parse(selection.detail.selectedItem.value);
    if (!this.firstLoad) {
      if (this.doneInit) {
        this.sendSortAnalytics();
      }
      this.doneInit = true;
      this._sortOptionSelectedUpdated();
    }
    this.firstLoad = false;
  }

  /**
   * On-click event to trigger analytics
   */
  sendSortAnalytics() {
    events.publish(events.shop.SEARCH_SORT_DROPDOWN); //Included for analytics
  }

  /**
   * Observer of the searchResults property
   * @private
   */
  _updateResults() {
    this.set("sortOptionSelected", 0);
    let results,
      productsArray = [];
    if (this.searchResults.length > 0) {
      this.noSort = false;
      results = this.searchResults[0].hasOwnProperty("resultsList")
        ? this.searchResults[0].resultsList.length
        : 0;
      this.searchResultsNumber = results;
      if (results > this.searchResultsPerPage) this.hideLoadMore = false;
      else this.hideLoadMore = true;
    } else {
      this.noSort = true;
    }
    let lazyLoad = this.shadowRoot.querySelector("#lazyLoad");
    if (lazyLoad) {
      lazyLoad.displayedResults = 0;
      lazyLoad.searchResultsPerPage = 36;
    }
    this._updateSearchForText();
    let skus = [];
    if (
      this.searchResults &&
      this.searchResults[0] &&
      this.searchResults[0].resultsList
    ) {
      for (let i = 0; i < this.searchResults[0].resultsList.length; i++) {
        this.searchResults[0].resultsList[i] = new Product(
          this.searchResults[0].resultsList[i]
        );
        skus.push(this.searchResults[0].resultsList[i].sku);
      }
    }
    this.$.resultsRepeater.render();
    events.publish("closeSpinner");

    ProductStatusService.getProductStatus(skus).then((productStatuses) => {
      for (let p = 0; p < productStatuses.length; p++) {
        for (let i = 0; i < this.searchResults[0].resultsList.length; i++) {
          if (
            this.searchResults[0].resultsList[i].sku == productStatuses[p].SKU
          ) {
            this.searchResults[0].resultsList[i].setMarketAttributes(
              productStatuses[p]
            );
            this.searchResults[0].resultsList[i].addPricingFromStatus(
              productStatuses[p],
              typeof user !== "undefined" && user ? user.priceType : "WRTL"
            );
            break;
          }
        }
      }
      this._setLocalStorageResultsData();
    });
  }

  /**
   * Used to reset sort dropdown to its first index
   * @private
   */
  _resetSortDropdown() {
    this.firstLoad = true;
    let sortByDropDownA = this.shadowRoot.querySelector("#sortbydropdownA");
    if (sortByDropDownA) {
      sortByDropDownA.initSelect = "1";
      sortByDropDownA.initSelect = "0";
    }
    let sortByDropDownB = this.shadowRoot.querySelector("#sortbydropdownB");
    if (sortByDropDownB) {
      sortByDropDownB.initSelect = "1";
      sortByDropDownB.initSelect = "0";
    }
  }

  /**
   * Used to manually lazyLoad in more search results
   */
  lazyLoad() {
    events.publish("openSpinner");
    setTimeout(() => {
      events.publish(events.shop.SEARCH_LOAD_MORE); //Included for analytics
      let lazyLoad = this.shadowRoot.querySelector("#lazyLoad");
      lazyLoad.displayedResults = 0;
      lazyLoad.searchResultsPerPage += 36;
      if (
        lazyLoad.searchResultsPerPage > this.searchResults[0].resultsList.length
      )
        this.hideLoadMore = true;
      this.set(
        "searchResults.0.resultsList.0.update",
        !this.searchResults[0].resultsList[0].update
      );
    }, 300);
    setTimeout(() => {
      events.publish("closeSpinner");
    }, 400);
  }

  /**
   * Used to redraw the screen on different screen sizes. Changes at 768 px.
   */
  onResize() {}

  /**
   * Used to sort product objects in their set order.
   * @param sort
   * @param order
   */
  sortBy(sort, order) {
    events.publish("openSpinner");
    for (let i = 0; i < this.searchResults.length; i++) {
      if (this.searchResults[i].resultsList) {
        let resultsList = this.searchResults[i].resultsList.sort((a, b) => {
          if (sort === "title") {
            return order === "desc"
              ? b[sort].localeCompare(a[sort])
              : a[sort].localeCompare(b[sort]);
          } else if (sort === "price") {
            return order === "desc"
              ? this._getPricingBasedOnLogin(a) -
                  this._getPricingBasedOnLogin(b)
              : this._getPricingBasedOnLogin(b) -
                  this._getPricingBasedOnLogin(a);
          } else if (sort === "pv") {
            return order === "desc"
              ? this._getPSVBasedOnLogin(a) - this._getPSVBasedOnLogin(b)
              : this._getPSVBasedOnLogin(b) - this._getPSVBasedOnLogin(a);
          } else if (sort === "best") {
            return b["searchScore"] - a["searchScore"];
          } else {
            return order === "desc"
              ? sortValue(a[sort]) - sortValue(b[sort])
              : sortValue(b[sort]) - sortValue(a[sort]);
          }
        });
        this.set("searchResults." + i + ".resultsList", []);
        this.set("searchResults." + i + ".resultsList", resultsList);
      }
      events.publish("closeSpinner");
    }

    let lazyLoad = this.shadowRoot.querySelector("#lazyLoad");
    if (lazyLoad) {
      lazyLoad.displayedResults = 0;
    }

    if (
      !!this.searchResults &&
      this.searchResults.length > 0 &&
      this.searchResults[0].resultsList &&
      this.searchResults[0].resultsList.length > 0
    ) {
      this.set(
        "searchResults.0.resultsList.0.update",
        !this.searchResults[0].resultsList[0].update
      );
    }

    function sortValue(item) {
      if (typeof item === "undefined" || item === null) {
        return 0;
      }

      return item;
    }
  }

  /**
   * Used to get the correct price based on your login price type
   * @param productObject
   * @returns {number}
   * @private
   */
  _getPricingBasedOnLogin(productObject) {
    let priceType = "WRTL";
    if (this.isBase(productObject)) {
      priceType = "min-WRTL";
    }
    let productPrice = 0;
    if (UserService.isLoggedIn()) {
      priceType = ShopContextService.getPriceType(false, UserService.getUser());
    }
    if (productObject) {
      if (productObject.hasOwnProperty("priceMap")) {
        Object.keys(productObject.priceMap).forEach((itemPriceType) => {
          if (itemPriceType === priceType) {
            productPrice = productObject.priceMap[itemPriceType];
          }
        });
      }
    }

    return productPrice;
  }

  /**
   * Used to get the correct PSV value based on your login price type
   * @param productObject
   * @returns {number}
   * @private
   */
  _getPSVBasedOnLogin(productObject) {
    let priceType = "WRTL";
    if (this.isBase(productObject)) {
      priceType = "min-WRTL";
    }
    let productPSV = 0;
    if (UserService.isLoggedIn()) {
      priceType = ShopContextService.getPriceType(false, UserService.getUser());
    }
    if (productObject) {
      if (productObject.hasOwnProperty("pvMap")) {
        Object.keys(productObject.pvMap).forEach((pvPriceType) => {
          if (pvPriceType === priceType) {
            productPSV = productObject.pvMap[pvPriceType];
          }
        });
      }
    }

    return productPSV;
  }

  /**
   * Used to remove passed in sort options from list if not logged in as a distributor
   * @param sortOption
   * @private
   */
  _removeSortOptionIfNotDistributor(sortOption) {
    if (this.translations) {
      let user = UserService.getUser();
      if (
        (!UserService.isLoggedIn() || !user.isDistributor()) &&
        this.translations.sortOptions
      ) {
        for (let i = 0; i < this.translations.sortOptions.length; i++) {
          if (this.translations.sortOptions[i].id === sortOption) {
            this.translations.sortOptions.splice(i, 1);
          }
        }
      }
    }
  }

  /**
   * Publishes closeResultsPage event if back button is clicked
   */
  back() {
    events.publish("closeResultsPage");
  }

  /**
   * Used to keep track of the results data that was last pulled so we can navigate to other pages and come back
   * @private
   */
  _setLocalStorageResultsData() {
    let searchResults = SitesSessionStorageService.getItem(SEARCH_TERM_KEY);
    searchResults = !searchResults ? {} : JSON.parse(searchResults);
    if (this.query) searchResults.query = this.query;
    if (
      this.searchResults &&
      !$.isEmptyObject(this.searchResults) &&
      this.searchResults.length > 0
    )
      searchResults.searchResults = this.searchResults;

    SitesSessionStorageService.writeItem(
      SEARCH_TERM_KEY,
      JSON.stringify(searchResults)
    );
  }

  /**
   * Used to reset the search result object saved in localStorage
   * @private
   */
  _resetLocaleStorageResultsData() {
    SitesSessionStorageService.removeItem(SEARCH_TERM_KEY);
  }

  /**
   * Checks to see if there is results data set in locale storage and sets the appropriate properties to their previous states
   * @private
   */
  _checkLocalStorageResultsData() {
    let resultsData = SitesSessionStorageService.getItem(SEARCH_TERM_KEY);
    if (resultsData) {
      resultsData = JSON.parse(resultsData);
      this.query = resultsData.query;
      this.searchResults = resultsData.searchResults;
    }

    this._resetLocaleStorageResultsData();
  }

  /**
   * Checks to see if we have passed the limit before we need to lazy load
   * @returns {boolean}
   */
  loadLimit() {
    if (this.searchResultsPerPage === undefined) this.searchResultsPerPage = 36;
    if (this.displayedResults === undefined) this.displayedResults = 0;
    if (this.displayedResults === this.searchResultsPerPage) return false;
    return ++this.displayedResults <= this.searchResultsPerPage;
  }

  /**
   * Updates the "search results for" translation.
   */
  _updateSearchForText() {
    const SEARCH_TERM = "%searchTerm%";
    const SEARCH_RESULTS_NUMBER = "%searchResultsNumber%";
    this.searchResultsFor = this.translations.searchResultsFor
      .replace(SEARCH_TERM, '<span id="query">"' + this.query + '"</span>')
      .replace(
        SEARCH_RESULTS_NUMBER,
        '<span id="results-num">' + this.searchResultsNumber + "</span>"
      );
  }

  /**
   * See if the SKU is a base SKU.
   */
  isBase(product) {
    return product.sku.substring(2, 4) === "55";
  }
}

customElements.define(NsSearchResultsPage.is, NsSearchResultsPage);
